import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import {useContext, useEffect, useState} from 'react';
import Layout from 'src/components/layout/Layout';
import {PageHeader} from 'src/components/layout/PageHeader';
import {AuthContext} from 'src/store/Auth/AuthContext';
import {useQueryStringFirst} from 'src/utility/CustomHooks/useQueryNumber';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {useSnackbar} from 'notistack';
import {UVConfirmDialog} from 'src/components/UserViewComponents/UIItems/Dialogs/UVConfirmDialog/UVConfirmDialog';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '94%',
    margin: '15px auto',
  },
  headerRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.5% 3%',
    width: '100%',
  },
  headerTitle: {
    fontWeight: 600,
    color: 'var(--primary-dark-green)',
    fontSize: '1.5rem',
  },
  contentRoot: {
    margin: '0%',
    overflow: 'auto',
  },
}));

export const ItemGroups = () => {
  const classes = useStyles();
  const authUser = useContext(AuthContext);
  const projectId = useQueryStringFirst('projectId');

  const [groups, setGroups] = useState([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteGroupState, setDeleteGroupState] = useState<string>();

  const {enqueueSnackbar} = useSnackbar();
  const open = Boolean(anchorEl);

  const [selectedGroup, setSelectedGroup] = useState<any>();

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    getItemGroupList();
  }, []);

  async function getItemGroupList() {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_APOLLO_SERVER_URL.split('/graphql')[0]}/rest/com-groups/v1/list`,
        {
          project_id: projectId,
        },
        {
          headers: {
            Authorization: `Bearer ${authUser?.access_token?.token}`,
          },
        }
      );
      setGroups(Array.isArray(res.data?.result) ? res.data?.result : []);
    } catch (err) {}
  }

  const handleGroupRefresh = async (name: string, folderId: string) => {
    try {
      enqueueSnackbar('Synchronizing the group...', {variant: 'info'});
      const res = await axios.post(
        `${process.env.REACT_APP_APOLLO_SERVER_URL.split('/graphql')[0]}/rest/com-groups/v1/create`,
        {
          project_id: projectId,
          folder_id: folderId,
          group_name: name,
        },
        {
          headers: {
            Authorization: `Bearer ${authUser?.access_token?.token}`,
          },
        }
      );

      enqueueSnackbar('Group Refreshed', {variant: 'success'});
    } catch (err) {
      enqueueSnackbar('Cannot Refresh the group', {variant: 'error'});
    }
  };

  const handleGroupBackupCreate = async (groupId: string) => {
    try {
      enqueueSnackbar('Creating backup point...', {variant: 'info'});

      const res = await axios.post(
        `${
          process.env.REACT_APP_APOLLO_SERVER_URL.split('/graphql')[0]
        }/rest/backups/v1/project-backup/project/point/create-comp-group-backup`,
        {
          project_id: projectId,
          name: `Backup from Group ${groupId}`,
          description: '',
          comp_group_id: groupId,
        },
        {
          headers: {
            Authorization: `Bearer ${authUser?.access_token?.token}`,
          },
        }
      );

      enqueueSnackbar('Backup point created', {variant: 'success'});
    } catch (err) {}
  };

  const handleGroupDelete = async (groupId: string) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_APOLLO_SERVER_URL.split('/graphql')[0]}/rest/com-groups/v1/delete`,
        {
          group_id: groupId,
        },
        {
          headers: {
            Authorization: `Bearer ${authUser?.access_token?.token}`,
          },
        }
      );
    } catch (err) {
      enqueueSnackbar('Cannot delete the group', {variant: 'error'});
    }
  };

  return (
    <Layout>
      <PageHeader>
        <Box className={classes.headerRoot}>
          <Typography className={classes.headerTitle}>Userview Item Groups</Typography>
        </Box>
      </PageHeader>
      <Box className={classes.contentRoot}>
        <Table className={classes.root}>
          <TableHead>
            <TableRow style={{backgroundColor: '#7F8FA4'}}>
              <TableCell>Group ID</TableCell>
              <TableCell>Group Name</TableCell>
              <TableCell>Folder ID</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group: any) => (
              <TableRow>
                <TableCell>{group.id}</TableCell>
                <TableCell>{group.name}</TableCell>
                <TableCell>{group.root_folder_id}</TableCell>
                <TableCell>{group.updated_at}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={e => {
                      handleMenuClick(e);
                      setSelectedGroup(group);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => handleClose()}
          PaperProps={{
            style: {
              //maxHeight: 48 * 4.5,
              width: '20ch',
            },
          }}
        >
          {selectedGroup && (
            <>
              <MenuItem
                onClick={() => {
                  handleClose();
                  handleGroupRefresh(selectedGroup.name, selectedGroup.root_folder_id);
                }}
              >
                Sync Group 
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  handleGroupBackupCreate(selectedGroup.id);
                }}
              >
                Make Backup Point
              </MenuItem>
              <MenuItem
                onClick={() => {
                  //handleClose('');
                  setDeleteGroupState(selectedGroup.id);
                  handleClose();
                }}
              >
                Delete Group
              </MenuItem>
              <MenuItem>
                Download Backup Point
              </MenuItem>
            </>
          )}
        </Menu>
        <UVConfirmDialog
          dialogOpen={!!deleteGroupState}
          dialogCloseEvent={(e: any) => {
            if (e === true) {
              handleGroupDelete(deleteGroupState);
              setDeleteGroupState(undefined);
            } else {
              setDeleteGroupState(undefined);
            }
          }}
          title="Confirm to delete the group ?"
          text="It will remove the group linked to this folder"
        />
      </Box>
    </Layout>
  );
};
